import type { DisputeOutcome, PaymentRecord, RefundRecord } from '../types/app'
import { formatDay, formatPrice } from '../catalog/format'

const KIND: Record<PaymentRecord['kind'], string> = {
  deposit: 'Deposit',
  balance: 'Balance',
  full: 'Payment',
  package: 'Package',
}

// STRIPE_METHODS words what a client paid with through Stripe, by Stripe's
// payment method type; a card from a wallet is named by the wallet.
const STRIPE_METHODS: Record<string, string> = {
  card: 'by card',
  apple_pay: 'by Apple Pay',
  google_pay: 'by Google Pay',
  klarna: 'with Klarna',
  link: 'with Link',
  revolut_pay: 'with Revolut Pay',
  afterpay_clearpay: 'with Clearpay',
  paypal: 'with PayPal',
  amazon_pay: 'with Amazon Pay',
}

// how words the method after the amount; a card payment not taken through
// Harmovi was marked paid by hand, from her own reader. A Stripe payment
// without a recorded method is from before they were kept: all were cards.
const how = (p: PaymentRecord): string => {
  if (p.method === 'cash') return 'in cash'
  if (p.method === 'bank_transfer') return 'by bank transfer'
  if (!p.through_stripe) return 'by card (own reader)'
  const paidWith = p.stripe_wallet ?? p.stripe_method ?? 'card'
  return STRIPE_METHODS[paidWith] ?? 'through Stripe'
}

// day is the date part of an API timestamp, as the profile shows dates.
const day = (iso: string) => formatDay(iso.slice(0, 10))

// paymentTitle names a payment in the history: "Deposit · £20 by card".
export function paymentTitle(p: PaymentRecord): string {
  return `${KIND[p.kind]} · ${formatPrice(p.amount_pence)} ${how(p)}`
}

// paymentDetail is the line under it: when, and Stripe's fee when there was one.
export function paymentDetail(p: PaymentRecord): string {
  const parts = [`Paid ${day(p.paid_at)}`]
  if (p.fee_pence > 0) parts.push(`Stripe fee ${formatPrice(p.fee_pence)}`)
  return parts.join(' · ')
}

// refundLine words one refund: "Refunded £10 · 14 Sep · Left early".
export function refundLine(r: RefundRecord): string {
  const amount = formatPrice(r.amount_pence)
  let head: string
  if (r.status === 'failed') head = `Refund of ${amount} failed`
  else if (r.status === 'pending') head = `Refund of ${amount} pending`
  else head = `Refunded ${amount}`
  const parts = [head, day(r.refunded_at ?? r.created_at)]
  if (r.source === 'stripe') parts.push('made in Stripe')
  if (r.credits_removed > 0) parts.push(r.credits_removed === 1 ? '1 session removed' : `${r.credits_removed} sessions removed`)
  if (r.reason) parts.push(r.reason)
  return parts.join(' · ')
}

// DisputeTag is the tag beside a payment with a dispute: a warning while it
// is open or once it is lost, a quiet note when it was won.
export interface DisputeTag {
  text: string
  tone: 'danger' | 'quiet'
}

// disputeTag is that tag for a payment or purchase, or null without a dispute.
export function disputeTag(p: { disputed: boolean; dispute_outcome?: DisputeOutcome }): DisputeTag | null {
  if (p.dispute_outcome === 'lost') return { text: 'Dispute lost', tone: 'danger' }
  if (p.dispute_outcome === 'won') return { text: 'Dispute won', tone: 'quiet' }
  if (p.disputed) return { text: 'Disputed', tone: 'danger' }
  return null
}
